import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { TOUR } from "../constants/testIds";
import {
  Trash2, CheckCircle2, RefreshCw, LogOut, ChevronLeft, Shield, Eye, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "voyage_admin_pw";

const StatusBadge = ({ status }) => {
  const map = {
    new:       { label: "Новая",        cls: "bg-coral/15 text-coral" },
    processed: { label: "Обработана",   cls: "bg-teal/15 text-teal" },
  };
  const s = map[status] || map.new;
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
};

const LoginScreen = ({ onSuccess }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, {
        headers: { "X-Admin-Password": pw.trim() },
      });
      sessionStorage.setItem(STORAGE_KEY, pw.trim());
      onSuccess(pw.trim());
    } catch (err) {
      toast.error("Неверный пароль");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl border border-[#F0DFC1]"
      >
        <Link to="/" className="text-sm text-teal hover:underline inline-flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> На главную
        </Link>
        <div className="w-14 h-14 rounded-2xl bg-teal flex items-center justify-center mb-5">
          <Shield className="w-7 h-7 text-white" />
        </div>
        <h1 className="font-display text-3xl text-[#063B4E]">Админ-панель</h1>
        <p className="text-[#4A5A6B] mt-2 mb-6">
          Введите пароль администратора для доступа к заявкам.
        </p>
        <Label className="text-[#1B2A3B]">Пароль</Label>
        <Input
          data-testid={TOUR.adminPasswordInput}
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          className="mt-1.5 h-11 bg-[#FFF7EC] border-[#E9D9BE]"
          autoFocus
        />
        <Button
          type="submit"
          data-testid={TOUR.adminLoginBtn}
          disabled={busy}
          className="mt-5 w-full bg-teal hover:bg-[#0a616a] text-white rounded-full h-11 font-semibold"
        >
          Войти
        </Button>
      </form>
      <Toaster position="top-center" richColors />
    </div>
  );
};

const RequestDrawer = ({ req, onClose }) => {
  if (!req) return null;
  const t = req.tour || {};
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-[#063B4E]/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[#F0DFC1] flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-teal">Заявка</div>
            <div className="font-display text-2xl text-[#063B4E]">{req.name}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#FFF7EC]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-3 text-sm">
          <Row k="Телефон" v={req.phone} />
          {req.telegram && <Row k="Telegram" v={req.telegram} />}
          {req.destination && <Row k="Направление" v={req.destination} />}
          {req.budget && <Row k="Бюджет" v={req.budget} />}
          <Row k="Статус" v={<StatusBadge status={req.status} />} />
          <Row k="Источник" v={req.source} />
          <Row k="Создана" v={new Date(req.created_at).toLocaleString("ru-RU")} />
          {req.notes && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[#8A99A8] mb-1">Комментарий</div>
              <div className="p-3 rounded-lg bg-[#FFF7EC] border border-[#F0DFC1] whitespace-pre-wrap">{req.notes}</div>
            </div>
          )}
          {(t.hotel || t.country) && (
            <div className="pt-3 border-t border-[#F0DFC1]">
              <div className="text-xs uppercase tracking-widest text-coral mb-2">Тур из карточки</div>
              {t.hotel && <Row k="Отель" v={`${t.hotel}${t.stars ? " " + "★".repeat(t.stars) : ""}`} />}
              {t.country && <Row k="Страна" v={`${t.country}${t.city ? ", " + t.city : ""}`} />}
              {t.date && <Row k="Дата" v={t.date} />}
              {t.nights && <Row k="Ночей" v={t.nights} />}
              {t.food && <Row k="Питание" v={t.food} />}
              {t.airline && <Row k="Авиа" v={t.airline} />}
              {t.room && <Row k="Номер" v={t.room} />}
              {t.price && <Row k="Цена" v={`${t.price}${t.old_price ? ` (было ${t.old_price})` : ""}`} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="text-xs uppercase tracking-widest text-[#8A99A8] shrink-0 pt-0.5">{k}</div>
    <div className="text-[#1B2A3B] font-medium text-right">{v}</div>
  </div>
);

export default function AdminRequests() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async (pw) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tour-requests`, {
        headers: { "X-Admin-Password": pw },
      });
      setItems(r.data);
      setAuthed(true);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setPassword("");
        setAuthed(false);
      } else {
        toast.error("Не удалось загрузить заявки");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (password) load(password);
    // eslint-disable-next-line
  }, []);

  const handleLogin = (pw) => {
    setPassword(pw);
    load(pw);
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setAuthed(false);
    setItems([]);
  };

  const markProcessed = async (id) => {
    try {
      await axios.patch(`${API}/tour-requests/${id}/status`, { status: "processed" }, {
        headers: { "X-Admin-Password": password },
      });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status: "processed" } : x)));
      toast.success("Заявка помечена как обработанная");
    } catch {
      toast.error("Не удалось обновить статус");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Удалить заявку?")) return;
    try {
      await axios.delete(`${API}/tour-requests/${id}`, {
        headers: { "X-Admin-Password": password },
      });
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Заявка удалена");
    } catch {
      toast.error("Не удалось удалить");
    }
  };

  if (!authed) return <LoginScreen onSuccess={handleLogin} />;

  return (
    <div data-testid={TOUR.adminSection} className="min-h-screen bg-cream">
      <header className="bg-white border-b border-[#F0DFC1] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="p-2 rounded-lg hover:bg-[#FFF7EC]">
              <ChevronLeft className="w-5 h-5 text-[#063B4E]" />
            </Link>
            <div>
              <div className="text-xs uppercase tracking-widest text-teal">Админ</div>
              <div className="font-display text-xl text-[#063B4E]">Заявки клиентов</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => load(password)}
              variant="ghost"
              className="text-[#4A5A6B] hover:text-[#063B4E]"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Обновить
            </Button>
            <Button
              data-testid={TOUR.adminLogout}
              onClick={logout}
              variant="ghost"
              className="text-[#4A5A6B] hover:text-coral"
            >
              <LogOut className="w-4 h-4 mr-2" /> Выйти
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="mb-5 flex flex-wrap gap-3">
          <div className="rounded-2xl bg-white p-4 border border-[#F0DFC1] flex-1 min-w-[140px]">
            <div className="text-xs uppercase tracking-widest text-[#8A99A8]">Всего</div>
            <div className="font-display text-3xl text-[#063B4E]">{items.length}</div>
          </div>
          <div className="rounded-2xl bg-white p-4 border border-[#F0DFC1] flex-1 min-w-[140px]">
            <div className="text-xs uppercase tracking-widest text-coral">Новые</div>
            <div className="font-display text-3xl text-[#063B4E]">
              {items.filter((x) => x.status === "new").length}
            </div>
          </div>
          <div className="rounded-2xl bg-white p-4 border border-[#F0DFC1] flex-1 min-w-[140px]">
            <div className="text-xs uppercase tracking-widest text-teal">Обработано</div>
            <div className="font-display text-3xl text-[#063B4E]">
              {items.filter((x) => x.status === "processed").length}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[#F0DFC1] overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table data-testid={TOUR.adminTable} className="w-full text-sm">
              <thead>
                <tr className="bg-[#FFF7EC] text-left text-[#4A5A6B] text-[11px] uppercase tracking-widest">
                  <th className="p-4">Дата</th>
                  <th className="p-4">Клиент</th>
                  <th className="p-4">Телефон</th>
                  <th className="p-4">Направление</th>
                  <th className="p-4">Бюджет</th>
                  <th className="p-4">Статус</th>
                  <th className="p-4 text-right">Действия</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="p-10 text-center text-[#8A99A8]">
                      Пока нет заявок. Как только клиент оставит заявку — она появится здесь.
                    </td>
                  </tr>
                )}
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-[#F0DFC1] hover:bg-[#FFFCF5]">
                    <td className="p-4 text-[#4A5A6B] whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString("ru-RU", {
                        day: "2-digit", month: "2-digit", year: "2-digit",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                    <td className="p-4 font-semibold text-[#063B4E]">{r.name}</td>
                    <td className="p-4 text-[#1B2A3B]"><a href={`tel:${r.phone}`}>{r.phone}</a></td>
                    <td className="p-4 text-[#1B2A3B]">
                      {r.tour?.country || r.destination || <span className="text-[#8A99A8]">—</span>}
                    </td>
                    <td className="p-4 text-[#1B2A3B]">{r.budget || <span className="text-[#8A99A8]">—</span>}</td>
                    <td className="p-4"><StatusBadge status={r.status} /></td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={TOUR.adminView}
                          onClick={() => setSelected(r)}
                          className="p-2 rounded-lg hover:bg-teal/10 text-teal"
                          title="Просмотр"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {r.status !== "processed" && (
                          <button
                            data-testid={TOUR.adminMarkProcessed}
                            onClick={() => markProcessed(r.id)}
                            className="p-2 rounded-lg hover:bg-teal/10 text-teal"
                            title="Пометить обработанной"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={TOUR.adminDelete}
                          onClick={() => remove(r.id)}
                          className="p-2 rounded-lg hover:bg-coral/10 text-coral"
                          title="Удалить"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <RequestDrawer req={selected} onClose={() => setSelected(null)} />
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}
